/**
 * Voxelization adapter — the mesh→grid bridge feeding the E1 separation-profile
 * landmarker (ADR-010 §2 Phase B, §3 "hand topology analysis"). ONE
 * responsibility: turn a triangle mesh into E1's {@link VoxelGrid} contract.
 * It runs the vendored Voxel-Skinner voxelizer (SAT shell + flood-fill interior)
 * and bridges its output to E1:
 *   (a) every occupied voxel (INTERIOR ∪ BOUNDARY) is remapped from the VS-native
 *       index `z*resY*resX + y*resX + x` into E1's `x + nx*(y + ny*z)` — decoded
 *       then re-encoded through {@link vsIndexToE1}, the load-bearing bridge;
 *   (b) `dims = [resX,resY,resZ]` and `voxelSize` are carried across;
 *   (c) a wrist seed is derived from an INJECTED seam (see below).
 * The returned grid passes E1's `validateVoxelGrid` — the analyzer never sees a
 * mesh, and this module never places joints or writes jointFrames (out of scope).
 *
 * wristSeed is an injected seam so a later skin-weight isolation step (E2b,
 * GLB/adapter side) can drive it: callers pass EITHER explicit E1-convention
 * `wristSeedVoxels`, OR a `cutPlane` at the wrist (seed = the occupied voxels
 * whose centers lie within one voxelSize of the plane on the hand side, the
 * normal pointing toward the hand). Exactly one is required.
 *
 * Fail-loud (ADR-010 §7, LAWS "fail loudly over silent fallback"): an empty
 * mesh, a non-triangle index/vertex count, non-finite coordinates, degenerate
 * (zero-volume) bounds, a bad wrist-seed selector, or a cutPlane that seeds zero
 * voxels all throw {@link InvalidMeshError} — never a silent empty grid.
 *
 * Depends on: hands/voxel-grid (E1 contract), hands/voxel/voxelizer (vendored),
 * ir/errors (KernelError). Depended on by: kernel index, and (later) the E2b
 * GLB/skin-weight isolation slice in adapter-three/scripts (out of scope here).
 *
 * Credit: the voxelizer it drives is a vendored port of `@p0qp0q/voxel-skinner`
 * (Dionne & de Lasa geodesic voxel-binding lineage) — see hands/voxel/.
 */
import { KernelError } from '../ir/errors.js';
import {
  validateVolumeGrid,
  validateVoxelGrid,
  voxelCoords,
  voxelIndex,
  type GridRepair,
  type VolumeGrid,
  type VoxelGrid,
} from './voxel-grid.js';
import {
  occupiedVoxelIndices,
  voxelize,
  type Indices,
  type Positions,
} from './voxel/voxelizer.js';

export type { Indices, Positions } from './voxel/voxelizer.js';

/**
 * A wrist cut plane in world space. `normal` points toward the HAND; the seed is
 * the occupied voxels whose centers sit within one voxelSize of the plane on
 * that side. Supplied by E2b's skin-weight isolation step in the real pipeline.
 */
export interface CutPlane {
  readonly point: readonly [number, number, number];
  readonly normal: readonly [number, number, number];
}

/** Options for {@link voxelizeMesh}. Exactly one wrist-seed source is required. */
export interface VoxelizeMeshOptions {
  /** Voxels along the longest mesh axis (default 64). Must be a positive integer. */
  readonly resolution?: number;
  /** Explicit E1-convention wrist-seed voxel indices (E2b supplies these). */
  readonly wristSeedVoxels?: Iterable<number>;
  /** A wrist cut plane; seed = hand-side occupied voxels within one voxelSize. */
  readonly cutPlane?: CutPlane;
}

/**
 * A mesh that cannot become a valid grid — a producer/caller bug, fail-loud
 * (ADR-010 §7). Mirrors E1's InvalidVoxelGridError: carries every issue found.
 */
export class InvalidMeshError extends KernelError {
  constructor(readonly issues: readonly string[]) {
    super(
      `Mesh cannot be voxelized (${issues.length} issue(s)):\n` +
        issues.map((i) => `  - ${i}`).join('\n'),
    );
  }
}

/**
 * Largest 6-connected component of a set of occupied E1 indices — 6-connectivity
 * to match the analyzer's geodesic flood, so "main component" here is exactly
 * what its BFS can reach. Deterministic: ascending-index BFS seeds; ties broken
 * by the earlier-seeded component.
 */
function largestComponent6(occupied: ReadonlySet<number>, dims: readonly [number, number, number]): Set<number> {
  const unvisited = new Set([...occupied].sort((a, b) => a - b));
  let best = new Set<number>();
  while (unvisited.size > 0) {
    const start = unvisited.values().next().value as number;
    unvisited.delete(start);
    const comp = new Set<number>([start]);
    const queue = [start];
    while (queue.length > 0) {
      const i = queue.shift()!;
      const [x, y, z] = voxelCoords(i, dims);
      for (const [dx, dy, dz] of [[1, 0, 0], [-1, 0, 0], [0, 1, 0], [0, -1, 0], [0, 0, 1], [0, 0, -1]] as const) {
        const nx = x + dx;
        const ny = y + dy;
        const nz = z + dz;
        if (nx < 0 || ny < 0 || nz < 0 || nx >= dims[0] || ny >= dims[1] || nz >= dims[2]) continue;
        const ni = voxelIndex(nx, ny, nz, dims);
        if (!unvisited.has(ni)) continue;
        unvisited.delete(ni);
        comp.add(ni);
        queue.push(ni);
      }
    }
    if (comp.size > best.size) best = comp;
  }
  return best;
}

/**
 * Bridge ONE Voxel-Skinner-native linear index into E1's `x + nx*(y + ny*z)`
 * convention: decode with the VS layout (`z*resY*resX + y*resX + x`), re-encode
 * with E1's {@link voxelIndex} over `dims = [resX,resY,resZ]`. The two encodings
 * are algebraically equal for these dims, but going through decode+re-encode is
 * what keeps the bridge correct if either convention ever changes — and what a
 * transposed remap (e.g. swapping resX/resZ) fails on an asymmetric grid.
 */
export function vsIndexToE1(vsIndex: number, resX: number, resY: number, resZ: number): number {
  const x = vsIndex % resX;
  const y = Math.floor(vsIndex / resX) % resY;
  const z = Math.floor(vsIndex / (resX * resY));
  const dims: readonly [number, number, number] = [resX, resY, resZ];
  return voxelIndex(x, y, z, dims);
}

/** Options for {@link voxelizeVolume} — the seedless shared substrate. */
export interface VoxelizeVolumeOptions {
  /** Voxels along the longest mesh axis (default 64). Must be a positive integer. */
  readonly resolution?: number;
}

/**
 * Turn a triangle mesh into the seedless {@link VolumeGrid} substrate (ADR-021
 * §4): occupancy + world embedding, no wrist seed. This is the shared service
 * with two consumers — the hand pipeline ({@link voxelizeMesh} layers the wrist
 * seed on top) and the chest-integrity weight repair, which measures geodesic
 * bone support through the volume and has no cut to seed. Same fail-loud
 * contract as voxelizeMesh for everything mesh-shaped: structural mesh issues,
 * degenerate bounds, a bad resolution, or zero occupied voxels all throw
 * {@link InvalidMeshError}.
 */
export function voxelizeVolume(
  positions: Positions,
  indices: Indices,
  options: VoxelizeVolumeOptions = {},
): VolumeGrid {
  const resolution = options.resolution ?? 64;
  if (!Number.isInteger(resolution) || resolution < 1) {
    throw new InvalidMeshError([`resolution must be a positive integer, got ${resolution}`]);
  }

  // --- mesh structural validation (fail-loud, collect every issue) ---
  validateMesh(positions, indices);

  // --- degenerate (zero-volume) bounds ---
  assertNonDegenerateBounds(positions);

  // --- voxelize + bridge to E1 ---
  const grid = voxelize(positions, indices, resolution);
  const dims: readonly [number, number, number] = [grid.resX, grid.resY, grid.resZ];
  const occupied = new Set<number>();
  for (const vi of occupiedVoxelIndices(grid)) {
    occupied.add(vsIndexToE1(vi, grid.resX, grid.resY, grid.resZ));
  }
  if (occupied.size === 0) {
    throw new InvalidMeshError([
      'mesh voxelized to zero occupied voxels — nothing to analyze (resolution too low for the feature size?)',
    ]);
  }

  // The voxel→world transform: the voxelizer's own grid origin — the world
  // position of voxel (0,0,0)'s lower corner — paired with the carried-across
  // voxelSize gives the exact VOXEL-CENTER embedding (see voxelPointToWorld).
  const result: VolumeGrid = {
    dims,
    voxelSize: grid.voxelSize,
    occupied,
    origin: [grid.originX, grid.originY, grid.originZ],
  };
  validateVolumeGrid(result);
  return result;
}

/**
 * Turn a triangle mesh into E1's {@link VoxelGrid}. See the module header for the
 * full contract. `positions` is a flat `[x,y,z,...]` array; `indices` gives 3
 * indices per triangle (or null for non-indexed). Returns a grid that passes
 * `validateVoxelGrid`; throws {@link InvalidMeshError} on any fail-loud trigger.
 */
export function voxelizeMesh(
  positions: Positions,
  indices: Indices,
  options: VoxelizeMeshOptions,
): VoxelGrid {
  // --- wrist-seed selector: exactly one of wristSeedVoxels / cutPlane ---
  const hasExplicit = options.wristSeedVoxels !== undefined;
  const hasPlane = options.cutPlane !== undefined;
  if (hasExplicit === hasPlane) {
    throw new InvalidMeshError([
      'exactly one wrist-seed source is required: provide `wristSeedVoxels` OR `cutPlane`, got ' +
        (hasExplicit ? 'both' : 'neither'),
    ]);
  }

  // --- cutPlane structural validation + unit normal ---
  let plane: { readonly p: readonly [number, number, number]; readonly n: readonly [number, number, number] } | null = null;
  if (options.cutPlane !== undefined) {
    plane = normalizedCutPlane(options.cutPlane);
  }

  // --- the shared substrate: occupancy + world embedding ---
  const volume = voxelizeVolume(
    positions,
    indices,
    options.resolution !== undefined ? { resolution: options.resolution } : {},
  );
  const dims = volume.dims;
  const voxelSize = volume.voxelSize;
  const occupied = volume.occupied;
  const origin = volume.origin!;

  // --- plane seeding over the E1 grid, voxel-CENTER convention ---
  const planeSeed = new Set<number>();
  // Signed plane distance per occupied E1 index — kept for the re-band fallback.
  const planeDist = plane !== null ? new Map<number, number>() : null;
  if (plane !== null && planeDist !== null) {
    for (const e1 of occupied) {
      const [x, y, z] = voxelCoords(e1, dims);
      const cx = origin[0]! + (x + 0.5) * voxelSize;
      const cy = origin[1]! + (y + 0.5) * voxelSize;
      const cz = origin[2]! + (z + 0.5) * voxelSize;
      const dSigned =
        (cx - plane.p[0]) * plane.n[0] + (cy - plane.p[1]) * plane.n[1] + (cz - plane.p[2]) * plane.n[2];
      planeDist.set(e1, dSigned);
      if (dSigned >= 0 && dSigned <= voxelSize) planeSeed.add(e1);
    }
  }

  // --- wrist seed from the injected seam ---
  const repairs: GridRepair[] = [];
  let wristSeed: Set<number>;
  if (plane !== null && planeDist !== null) {
    if (planeSeed.size === 0) {
      // The declared band is empty. Measured cause (barbarian R, 2026-07-28): a
      // stray proximal mesh island (bracer-edge ring) owned the region's minimum
      // projection, so the isolation clamped the plane into the vertex-free gap
      // between ring and palm. The repair: RE-BAND onto the MAIN voxel component
      // — slide distally to that component's first occupied slab — and record
      // the liberty on the grid. When the whole volume sits behind the plane,
      // the original fail-loud refusal stands.
      const main = largestComponent6(occupied, dims);
      let dMin = Infinity;
      for (const i of main) {
        const d = planeDist.get(i)!;
        if (d >= 0 && d < dMin) dMin = d;
      }
      if (Number.isFinite(dMin)) {
        for (const i of main) {
          const d = planeDist.get(i)!;
          if (d >= dMin && d <= dMin + voxelSize) planeSeed.add(i);
        }
        repairs.push({
          code: 'wrist-band-rebanded',
          detail: {
            slidVoxels: dMin / voxelSize,
            mainComponentVoxels: main.size,
            occupiedVoxels: occupied.size,
          },
        });
      }
    }
    if (planeSeed.size === 0) {
      throw new InvalidMeshError([
        'cutPlane seeded zero voxels — no occupied voxel center lies within one voxelSize on the hand side',
      ]);
    }
    wristSeed = planeSeed;
  } else {
    wristSeed = new Set<number>(options.wristSeedVoxels);
  }

  const result: VoxelGrid =
    repairs.length > 0
      ? { dims, voxelSize, occupied, wristSeed, origin, repairs }
      : { dims, voxelSize, occupied, wristSeed, origin };
  // Final contract guarantee (throws InvalidVoxelGridError if a producer bug —
  // e.g. explicit seed indices out of range — slipped through).
  validateVoxelGrid(result);
  return result;
}

/** Structural mesh validation — throws {@link InvalidMeshError} with all issues. */
function validateMesh(positions: Positions, indices: Indices): void {
  const issues: string[] = [];
  const posLen = positions.length;

  if (posLen === 0) {
    issues.push('positions is empty — no mesh to voxelize');
  } else if (posLen % 3 !== 0) {
    issues.push(`positions length ${posLen} is not a multiple of 3 (whole [x,y,z] vertices)`);
  }

  let nonFinite = 0;
  for (let i = 0; i < posLen; i += 1) {
    if (!Number.isFinite(positions[i]!)) nonFinite += 1;
  }
  if (nonFinite > 0) issues.push(`positions has ${nonFinite} non-finite coordinate(s)`);

  const vertexCount = Math.floor(posLen / 3);
  if (indices !== null && indices !== undefined) {
    const idxLen = indices.length;
    if (idxLen === 0) {
      issues.push('indices is empty — no triangles');
    } else if (idxLen % 3 !== 0) {
      issues.push(`indices length ${idxLen} is not a multiple of 3 (whole triangles)`);
    }
    let badIdx = 0;
    for (let i = 0; i < idxLen; i += 1) {
      const v = indices[i]!;
      if (!Number.isInteger(v) || v < 0 || v >= vertexCount) badIdx += 1;
    }
    if (badIdx > 0) {
      issues.push(`indices has ${badIdx} value(s) out of range for ${vertexCount} vertices`);
    }
  } else if (posLen > 0 && posLen % 9 !== 0) {
    issues.push(`non-indexed positions length ${posLen} is not a multiple of 9 (whole triangles)`);
  }

  const triCount =
    indices !== null && indices !== undefined
      ? Math.floor(indices.length / 3)
      : Math.floor(posLen / 9);
  if (issues.length === 0 && triCount < 1) issues.push('mesh has zero triangles');

  if (issues.length > 0) throw new InvalidMeshError(issues);
}

/** Throw if any axis of the mesh bounding box has zero extent (zero-volume). */
function assertNonDegenerateBounds(positions: Positions): void {
  const min: [number, number, number] = [Infinity, Infinity, Infinity];
  const max: [number, number, number] = [-Infinity, -Infinity, -Infinity];
  for (let i = 0; i < positions.length; i += 3) {
    for (let a = 0; a < 3; a += 1) {
      const c = positions[i + a]!;
      if (c < min[a]!) min[a] = c;
      if (c > max[a]!) max[a] = c;
    }
  }
  const flat: string[] = [];
  const axisName = ['x', 'y', 'z'];
  for (let a = 0; a < 3; a += 1) {
    if (!(max[a]! - min[a]! > 0)) flat.push(axisName[a]!);
  }
  if (flat.length > 0) {
    throw new InvalidMeshError([
      `degenerate (zero-volume) bounds: axis/axes [${flat.join(', ')}] have zero extent`,
    ]);
  }
}

/** Validate a cut plane and return it with a unit normal. Throws on bad input. */
function normalizedCutPlane(plane: CutPlane): {
  readonly p: readonly [number, number, number];
  readonly n: readonly [number, number, number];
} {
  const { point, normal } = plane;
  const issues: string[] = [];
  if (!(Number.isFinite(point[0]) && Number.isFinite(point[1]) && Number.isFinite(point[2]))) {
    issues.push('cutPlane.point has non-finite components');
  }
  if (!(Number.isFinite(normal[0]) && Number.isFinite(normal[1]) && Number.isFinite(normal[2]))) {
    issues.push('cutPlane.normal has non-finite components');
  }
  const mag = Math.hypot(normal[0], normal[1], normal[2]);
  if (!(mag > 0)) issues.push('cutPlane.normal is a zero-length vector (no hand side to seed)');
  if (issues.length > 0) throw new InvalidMeshError(issues);
  return { p: point, n: [normal[0] / mag, normal[1] / mag, normal[2] / mag] };
}
